import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import { useMemo } from 'react'
import {
  conditionSearchParam,
  decodeConditions,
  encodeConditions,
} from '@/components/condition-filter'
import { creditNoteConditionFields } from '@/components/condition-filter/accounting-fields'
import type { InvoiceFilters } from '@/features/accounting-invoices/api'
import { InvoiceList } from '@/features/accounting-invoices/invoice-list'
import { hasGrant } from '@/lib/me'

export const Route = createFileRoute('/_authenticated/accounting/credit-notes')({
  validateSearch: (search: Record<string, unknown>): InvoiceFilters => ({
    c: conditionSearchParam(search.c),
    match: search.match === 'any' ? 'any' : undefined,
    postingStatus: oneOf(search.postingStatus, ['draft', 'posted', 'cancelled']),
    paymentStatus: oneOf(search.paymentStatus, ['unpaid', 'partially_paid', 'paid', 'overdue']),
    status: oneOf(search.status, ['draft', 'confirmed', 'partially_paid', 'paid', 'cancelled']),
    customer: typeof search.customer === 'string' && search.customer ? search.customer : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    overdue: search.overdue === '1' || search.overdue === true ? true : undefined,
    page: positiveInteger(search.page),
    perPage: pageSize(search.perPage),
    sort: oneOf(search.sort, ['invoice_date', 'document_number', 'customer', 'total']),
    direction:
      search.direction === 'asc' || search.direction === 'desc' ? search.direction : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'accounting_invoice.view'))
      throw redirect({ to: '/accounting' })
  },
  component: CreditNotesRoute,
})

function CreditNotesRoute() {
  const navigate = useNavigate()
  // credit-notes.new.tsx is a child route; without an Outlet its URL renders this list.
  const children = useChildMatches()
  const search = Route.useSearch()
  const { conditions, match } = useMemo(
    () => decodeConditions({ c: search.c, match: search.match }, creditNoteConditionFields),
    [search.c, search.match],
  )
  const params = { ...search, conditions, match }
  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <InvoiceList
      params={params}
      documentType="credit_note"
      onParamsChange={(next) =>
        void navigate({
          to: '/accounting/credit-notes',
          search: {
            ...next,
            ...encodeConditions(next.conditions ?? [], next.match ?? 'all'),
            conditions: undefined,
          },
        })
      }
    />
  )
}

function oneOf<const T extends readonly string[]>(
  value: unknown,
  values: T,
): T[number] | undefined {
  return typeof value === 'string' && values.includes(value) ? (value as T[number]) : undefined
}

function positiveInteger(value: unknown): number | undefined {
  const parsed = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value
  return typeof parsed === 'number' && Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

function pageSize(value: unknown): number | undefined {
  const parsed = positiveInteger(value)
  return parsed === 10 || parsed === 25 || parsed === 50 || parsed === 100 ? parsed : undefined
}
